import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import './App.css';
import { primaryInput } from 'detect-it';
import { supportedBrowsers } from './globals/variables';
import AnimatedRoutes from './AnimatedRoutes';
import Navbar from './components/Navbar/Navbar';
import { AnimatePresence } from 'framer-motion';
import CustomCursor from './components/CustomCursor/CustomCursor'
import HomepageOverlayTransition from './components/HomepageOverlayTransition/HomepageOverlayTransition';
import ArchiveOverlayTransition from './components/ArchiveOverlayTransition/ArchiveOverlayTransition';
import HomeToArchiveTransition from './components/HomeToArchiveTransition/HomeToArchiveTransition';
import Loader from './pages/loader/loader';
import { videoInStart, waitForLoaderMount } from './pages/loader/variables';
import LowPowerMode from './pages/LowPowerMode/LowPowerMode';

function App() {
  // Check the path immediately on boot to see if we are starting on the secret page
  const initIsSecret = window.location.pathname.toLowerCase().includes('/secret');

  const [cursorText, setCursorText] = useState("");
  const [hideCursor, setHideCursor] = useState(false);
  
  // Initialize state based on the boot path so the loader is bypassed immediately if needed
  const [isFirstLoad, setIsFirstLoad] = useState(!initIsSecret);
  const [firstLoadCover, setFirstLoadCover] = useState(initIsSecret ? true : null);
  
  const [isOutdatedSafari, setIsOutdatedSafari] = useState(false);
  const [isDeviceMobile, setIsDeviceMobile] = useState(null);
  const [isHomeScreen, setIsHomeScreen] = useState(false);
  const [triggerHomepageToProjectTransition, setTriggerHomepageToProjectTransition] = useState(false);
  const [triggerArchiveToHomepageTransition, setTriggerArchiveToHomepageTransition] = useState(false);
  const [homeToArchiveTransitionTrigger, setHomeToArchiveTransitionTrigger] = useState(false);
  const [isLowerPowerMode, setIsLowPowerMode] = useState(false);

  var nVer;
  var nAgt;
  var browserName;
  var fullVersion;
  var majorVersion;
  var nameOffset, verOffset, ix;


  // Run-once logic on boot
  useEffect(() => {
    console.log('%c Looking to see how this all works? Yeah I don\'t really know either... \n But hey, if you\'re interested, all design, development & motion by @DANNY.LINES \n \n Feel free to say hi.', 'background: black; color: red');

    // mobile check (fixed from window.width to window.innerWidth)
    if (window.innerWidth < 600) setIsDeviceMobile(true)
    else setIsDeviceMobile(false);
  }, [])

  // Timer logic that runs WHENEVER isFirstLoad becomes true
  useEffect(() => {
    if (isFirstLoad) {
      const timer1 = setTimeout(() => {
        setFirstLoadCover(true);
      }, waitForLoaderMount * 1000);

      const timer2 = setTimeout(() => {
        setIsFirstLoad(false);
      }, videoInStart * 1000);

      // Cleanup timers in case of sudden unmounts
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [isFirstLoad]);


  function semverCompare(a, b) {
    if (a.startsWith(b + "-")) return -1
    if (b.startsWith(a + "-")) return 1
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: "case", caseFirst: "upper" })
  }

  // This function checks to ensure that the browser being used is compatible with the site
  // The website makes use of 'dvh' in the css files (dynamic view height) which is only supported on certain browsers
  // The site was also only developed and tested on FF, Safari & Chrome (chrome being primary) so other browsers or non-updated versions
  // which dont support dvh return false from this function
  function getBrowserDetails() {
    nVer = navigator.appVersion;
    nAgt = navigator.userAgent;
    browserName = navigator.appName;
    fullVersion = '' + parseFloat(navigator.appVersion);
    majorVersion = parseInt(navigator.appVersion, 10);

    // In Opera, the true version is after "OPR" or after "Version"
    if ((verOffset = nAgt.indexOf("OPR")) != -1) {
      browserName = "Opera";
      fullVersion = nAgt.substring(verOffset + 4);
      if ((verOffset = nAgt.indexOf("Version")) != -1)
        fullVersion = nAgt.substring(verOffset + 8);
    }
    // In MS Edge, the true version is after "Edg" in userAgent
    else if ((verOffset = nAgt.indexOf("Edg")) != -1) {
      browserName = "Microsoft Edge";
      fullVersion = nAgt.substring(verOffset + 4);
    }
    // In MSIE, the true version is after "MSIE" in userAgent
    else if ((verOffset = nAgt.indexOf("MSIE")) != -1) {
      browserName = "Microsoft Internet Explorer";
      fullVersion = nAgt.substring(verOffset + 5);
    }
    // In Chrome, the true version is after "Chrome" 
    else if ((verOffset = nAgt.indexOf("Chrome")) != -1) {
      browserName = "Chrome";
      fullVersion = nAgt.substring(verOffset + 7);
    }
    // In Safari, the true version is after "Safari" or after "Version" 
    else if ((verOffset = nAgt.indexOf("Safari")) != -1) {
      browserName = "Safari";
      fullVersion = nAgt.substring(verOffset + 7);
      if ((verOffset = nAgt.indexOf("Version")) != -1)
        fullVersion = nAgt.substring(verOffset + 8);
    }
    // In Firefox, the true version is after "Firefox" 
    else if ((verOffset = nAgt.indexOf("Firefox")) != -1) {
      browserName = "Firefox";
      fullVersion = nAgt.substring(verOffset + 8);
    }
    // In most other browsers, "name/version" is at the end of userAgent 
    else if ((nameOffset = nAgt.lastIndexOf(' ') + 1) <
      (verOffset = nAgt.lastIndexOf('/'))) {
      browserName = nAgt.substring(nameOffset, verOffset);
      fullVersion = nAgt.substring(verOffset + 1);
      if (browserName.toLowerCase() == browserName.toUpperCase()) {
        browserName = navigator.appName;
      }
    }
    // trim the fullVersion string at semicolon/space if present
    if ((ix = fullVersion.indexOf(";")) != -1)
      fullVersion = fullVersion.substring(0, ix);
    if ((ix = fullVersion.indexOf(" ")) != -1)
      fullVersion = fullVersion.substring(0, ix);

    majorVersion = parseInt('' + fullVersion, 10);
    if (isNaN(majorVersion)) {
      fullVersion = '' + parseFloat(navigator.appVersion);
      majorVersion = parseInt(navigator.appVersion, 10);
    }


    console.log(''
      + 'Browser name  = ' + browserName + '<br>'
      + 'Full version  = ' + fullVersion + '<br>'
      + 'Major version = ' + majorVersion + '<br>'
      + 'navigator.appName = ' + navigator.appName + '<br>'
      + 'navigator.userAgent = ' + navigator.userAgent + '<br>'
    )

    let isSupported = semverCompare(fullVersion, String(supportedBrowsers[browserName.toLowerCase()]))
    if (isSupported == -1) {
      return false
    } else {
      return true
    }
  }

  useEffect(() => {
    console.log('CHANGING', isFirstLoad)
  }, [isFirstLoad])

  return (
    <>
      <div className="App">
        {
          getBrowserDetails() ?
            <Router>
              {primaryInput == "mouse" &&
                <CustomCursor cursorText={cursorText} hideCursor={hideCursor} />
              }
              <AnimatePresence>
                {/* Now purely dependent on state, not a hardcoded URL check */}
                {isFirstLoad &&
                  <Loader setIsLowPowerMode={setIsLowPowerMode} />
                }
              </AnimatePresence>
              <>
                <Navbar />
                {
                  isDeviceMobile !== null && !isDeviceMobile &&
                  <>
                    <HomepageOverlayTransition
                      triggerHomepageToProjectTransition={triggerHomepageToProjectTransition}
                    />
                    <ArchiveOverlayTransition
                      triggerArchiveToHomepageTransition={triggerArchiveToHomepageTransition}
                    />
                    <HomeToArchiveTransition
                      homeToArchiveTransitionTrigger={homeToArchiveTransitionTrigger}
                    />
                  </>
                }
              </>
              {
                firstLoadCover && !isLowerPowerMode &&
                <AnimatedRoutes
                  setCursorText={setCursorText}
                  isOutdatedSafari={isOutdatedSafari}
                  setIsHomeScreen={setIsHomeScreen}
                  setIsFirstLoad={setIsFirstLoad}
                  isFirstLoad={isFirstLoad}
                  setTriggerArchiveToHomepageTransition={setTriggerArchiveToHomepageTransition}
                  setTriggerHomepageToProjectTransition={setTriggerHomepageToProjectTransition}
                  setHomeToArchiveTransitionTrigger={setHomeToArchiveTransitionTrigger}
                  setHideCursor={setHideCursor}
                />
              }

            </Router>
            :
            // Screen for bouncing users on unsupported browsers/versions
            <div
              className='unsupported__wrapper'
              style={{
                color: "white",
                display: "flex",
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                width: '100vw',
                position: 'absolute',
                top: 0,
                left: 0
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: 'center',
                  fontFamily: 'var(--font-punchy)',
                  fontSize: "max(0.75rem, 11px)",
                }}
              >
                <span>DON PROD</span>
                <span>DIRECTOR SHOWCASE</span>
                <span>&nbsp;</span>
                <span>&nbsp;</span>
                <span>&nbsp;</span>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'white',
                  fontSize: "max(1rem, 10px)",
                  paddingLeft: "1rem",
                  paddingRight: "1rem",
                  color: "var(--color-highlighted)",
                  paddingBottom: '10vh',
                  textAlign: 'center',
                  fontSize: "2vw",
                  fontFamily: "var(--font-punchy)"
                }}
              >
                <span>You are utilising an un-supported browser to access this website.</span>
                <span>&nbsp;</span>
                {
                  Object.keys(supportedBrowsers).includes(browserName.toLowerCase()) ?
                    <>
                      <span >{"You are currently using " + browserName + ", version: " + fullVersion + "."} </span>
                      <span >{"The oldest version supported by this website is: " + String(supportedBrowsers[browserName.toLowerCase()])}</span>
                      <span>&nbsp;</span>
                      <span style={{ paddingBottom: "0.25rem" }}>Please update your browser, or use one of the browsers found below</span>
                    </>
                    :
                    <>
                      <span >{"You are currently using " + browserName + ", version: " + fullVersion + "."} </span>
                      <span>{"This browser is not supported at the moment, please see below for alternate browser choices."}</span>
                      <span>&nbsp;</span>
                    </>

                }


                <span>
                  The latest versions of <span style={{ color: 'white' }}>Safari</span>, <span style={{ color: 'white' }}>Firefox</span> and <span style={{ color: 'white' }}>Chrome</span> are currently supported. <b>Chrome is recommended for best user experience.</b></span>
              </div>
            </div>
        }
      </div>
    </>
  );
}

export default App;